
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useHackathon } from '../../hooks/useHackathon';
import { disableVotingPeriod, enableVotingPeriod, endVotingPeriod } from '../../services/hackathon.service';
import { useAuth } from '../../services/useAuth';
import { PageContainer } from '../../components/PageContainer';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import Tabs from '@mui/material/Tabs/Tabs';
import Tab from '@mui/material/Tab/Tab';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { IdeaPayload } from '../../services/idea.service';
import { aggregateVotes } from '../../services/vote.service';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Chip from '@mui/material/Chip/Chip';
import Avatar from '@mui/material/Avatar/Avatar';
import { useSessions } from '../../hooks/useSessions';
import { judgeCount, ResultScorecard, scoreInSession, sessionBreakdown, SessionScore } from '../../utils/results';

interface Row {
    ideaId: string;
    judgeAverage: number;
    judgeCount: number;
    participantAverage: number;
    // per-session numbers, shown when more than one session scored this category
    breakdown: SessionScore[];
}

const fmt = (value: number) => value ? value.toFixed(2) : '-';
const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

export const VotePage = () => {
    const { id } = useParams<{ id: string }>();

    const { hackathon } = useHackathon(id);
    const { sessions } = useSessions(id);

    const [activeTab, setActiveTab] = useState(0);
    // 'all' or a session id
    const [sessionFilter, setSessionFilter] = useState('all');

    const ideasMap = useMemo(() => {
        const map = new Map<string, IdeaPayload>();
        hackathon?.ideas.forEach(idea => map.set(idea._id, idea));
        return map;
    }, [hackathon])

    const selectedSession = sessions.find(s => s._id === sessionFilter);
    const sessionNames = useMemo(() => new Map(sessions.map(s => [s._id, s.name])), [sessions]);

    // categories shown: all, or the ones the selected session judges
    const categories = useMemo(() => {
        if (!hackathon) {
            return [];
        }
        return hackathon.categories
            .map((label, index) => ({ label, id: hackathon.categoryMeta[index]?._id }))
            .filter(c => !selectedSession || (!!c.id && selectedSession.categories.includes(c.id)));
    }, [hackathon, selectedSession]);

    const rowsFor = (category: { label: string, id?: string }): Row[] => {
        if (!hackathon?.results) {
            return [];
        }
        const result = hackathon.results.find(r => (category.id && (r as { categoryId?: string }).categoryId === category.id) || r.category === category.label);
        if (!result) {
            return [];
        }
        const rows = (result.ideas as unknown as ResultScorecard[])
            .filter(card => !selectedSession || selectedSession.ideas.includes(card.ideaId))
            .map(card => {
                if (selectedSession) {
                    const here = scoreInSession(card, selectedSession._id);
                    return {
                        ideaId: card.ideaId,
                        judgeAverage: here?.judgeAverage || 0,
                        judgeCount: here?.judgeCount || 0,
                        participantAverage: here?.participantAverage || 0,
                        breakdown: []
                    };
                }
                return {
                    ideaId: card.ideaId,
                    judgeAverage: card.judgeVoteAverage,
                    judgeCount: judgeCount(card),
                    participantAverage: card.participantVoteAverage,
                    breakdown: sessionBreakdown(card).filter(s => s.sessionId)
                };
            });
        return rows.sort((a, b) => {
            if (b.judgeAverage === a.judgeAverage && hackathon.participantScores) {
                return b.participantAverage - a.participantAverage;
            }
            return b.judgeAverage - a.judgeAverage;
        });
    };

    const { user } = useAuth();
    const isJudgeOrOrganizer = useMemo(() => {
        return user && hackathon && (hackathon.judges.some(j => j.email === user.email) || hackathon.organizers.some(o => o.email === user.email));
    }, [hackathon, user])

    if (!hackathon) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>No hackathon found</h1>
            </Stack>
        </PageContainer>
    }

    if (!isJudgeOrOrganizer) {
        return <PageContainer>
            <Alert severity="error">You are not authorized to view this page</Alert>
        </PageContainer>
    }

    if (!hackathon.categories || hackathon.categories.length === 0) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>No categories to vote on</h1>
            </Stack>
        </PageContainer>
    }

    const tab = Math.min(activeTab, Math.max(categories.length - 1, 0));
    const hasResults = !!hackathon.results && hackathon.results.length > 0;

    return (
        <PageContainer>
            <AppBreadcrumbs crumbs={[
                {
                    type: PageType.Home,
                    link: '/',
                    name: 'Home'
                },
                {
                    type: PageType.Hackathon,
                    link: `/h/${hackathon.slug}`,
                    name: hackathon.name
                },
                {
                    type: PageType.Results,
                    name: 'Voting Results'
                }
            ]} />
            <Stack spacing={2}>
                <div>
                    <h1>Voting results</h1>
                    <Typography color="text.secondary">{plural(hackathon.ideas.length, 'idea')} · {plural(hackathon.categories.length, 'category', 'categories')}{sessions.length > 1 ? ` · ${sessions.length} sessions` : ''}</Typography>
                </div>
                {
                    hackathon.votingPeriodOn && <Alert severity="info">Voting period is open</Alert>
                }
                <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
                    <Button variant="contained" onClick={() => {
                        // end voting period
                        endVotingPeriod(hackathon._id).then(() => setActiveTab(0));
                    }}>End Voting</Button>
                    <Button variant="outlined" disabled={!!hackathon.votingPeriodOn} onClick={() => {
                        enableVotingPeriod(hackathon._id).then(() => setActiveTab(0));
                    }}>Open Voting</Button>
                    <Button variant="outlined" disabled={!hackathon.votingPeriodOn} onClick={() => {
                        disableVotingPeriod(hackathon._id).then(() => setActiveTab(0));
                    }}>Close Voting</Button>
                    <Button variant="outlined" onClick={() => {
                        aggregateVotes(hackathon._id).then(() => setActiveTab(0));
                    }}>Refresh Results</Button>
                </Stack>

                {
                    sessions.length > 1 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="group" aria-label="Show results for">
                            {[{ _id: 'all', name: 'All sessions' }, ...sessions].map(s => (
                                <Chip
                                    key={s._id}
                                    label={s.name}
                                    color={sessionFilter === s._id ? 'primary' : 'default'}
                                    variant={sessionFilter === s._id ? 'filled' : 'outlined'}
                                    aria-pressed={sessionFilter === s._id}
                                    onClick={() => { setSessionFilter(s._id); setActiveTab(0); }} />
                            ))}
                        </Stack>
                    )
                }

                {!hasResults && <Alert severity="info">No results yet. They're calculated when voting ends, or click Refresh Results.</Alert>}
                {selectedSession && categories.length === 0 && <Alert severity="info">{selectedSession.name} doesn't judge any categories.</Alert>}

                <Tabs value={tab} onChange={(_, newValue) => setActiveTab(newValue)} variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    {
                        categories.map((category, index) => (
                            <Tab key={category.id || category.label} value={index} label={category.label} />
                        ))
                    }
                </Tabs>
                {
                    categories.map((category, index) => {
                        const rows = rowsFor(category);
                        return (
                            <TabPanel key={category.id || category.label} value={tab} index={index}>
                                {
                                    rows.length === 0
                                        ? <Typography color="text.secondary">No scores in this category yet.</Typography>
                                        : (
                                            <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                                {
                                                    rows.map((row, rank) => {
                                                        const idea = ideasMap.get(row.ideaId);
                                                        if (!idea) {
                                                            return null;
                                                        }
                                                        return (
                                                            <Card component="li" key={row.ideaId} sx={{
                                                                borderColor: rank === 0 ? 'secondary.main' : undefined,
                                                                borderWidth: rank === 0 ? 2 : 1
                                                            }}>
                                                                <CardContent sx={{ py: '16px !important' }}>
                                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                                        <Avatar sx={{
                                                                            width: 44,
                                                                            height: 44,
                                                                            fontWeight: 700,
                                                                            bgcolor: rank === 0 ? 'secondary.main' : rank < 3 ? 'primary.main' : 'action.selected',
                                                                            color: rank < 3 ? '#fff' : 'text.primary'
                                                                        }}>{rank + 1}</Avatar>
                                                                        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography variant={'h5'} component="h3">
                                                                                <Link to={`/h/${hackathon.slug}/idea/${idea._id}`} style={{ color: 'inherit' }}>{idea.title}</Link>
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary">{idea.members.map(m => m.username).join(', ')}</Typography>
                                                                        </Stack>
                                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                                                                            <Chip color="primary" label={`Judges ${fmt(row.judgeAverage)}${row.judgeCount ? ` · ${row.judgeCount}` : ''}`} />
                                                                            {
                                                                                hackathon.participantScores && <Chip color="secondary" variant="outlined" label={`Participants ${fmt(row.participantAverage)}`} />
                                                                            }
                                                                        </Stack>
                                                                    </Stack>
                                                                    {
                                                                        row.breakdown.length > 1 && (
                                                                            <Box sx={{ mt: 1.5, ml: { sm: 7.5 }, p: 1.5, borderRadius: '10px', backgroundColor: 'var(--surface-muted)' }}>
                                                                                <Typography variant="caption" color="text.secondary">Scored in {row.breakdown.length} sessions</Typography>
                                                                                {row.breakdown.map(s => (
                                                                                    <Stack key={s.sessionId || 'none'} direction="row" spacing={1} justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                                                                        <Typography variant="body2">{sessionNames.get(s.sessionId || '') || 'Removed session'}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            Judges {fmt(s.judgeAverage)} ({s.judgeCount}){hackathon.participantScores ? ` · Participants ${fmt(s.participantAverage)} (${s.participantCount})` : ''}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                ))}
                                                                            </Box>
                                                                        )
                                                                    }
                                                                </CardContent>
                                                            </Card>
                                                        )
                                                    })
                                                }
                                            </Stack>
                                        )
                                }
                            </TabPanel>
                        )
                    })
                }
            </Stack>
        </PageContainer>
    )
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({
    children,
    value,
    index,
    ...others
}: TabPanelProps) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            {...others}
        >
            {value === index && (
                <Stack spacing={2}>
                    {children}
                </Stack>
            )}
        </div>
    );
}
